import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { adminApi } from '@/lib/api';

const AdminResources = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', url: '', category: 'assessment', order: 0, is_active: true });

  useEffect(() => { fetchResources(); }, []);

  const fetchResources = async () => {
    try { const res = await adminApi.getFreeResources(); setResources(res.data); }
    catch (error) { toast.error('Failed to fetch resources'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setFormData({ title: '', description: '', url: '', category: 'assessment', order: resources.length, is_active: true }); setEditing(null); };

  const handleEdit = (r) => { setEditing(r); setFormData(r); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.url) { toast.error('Title and URL required'); return; }
    try {
      if (editing) { await adminApi.updateFreeResource(editing.id, formData); toast.success('Updated'); }
      else { await adminApi.createFreeResource(formData); toast.success('Created'); }
      setDialogOpen(false); resetForm(); fetchResources();
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await adminApi.deleteFreeResource(id); toast.success('Deleted'); fetchResources(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const groupedResources = { assessment: resources.filter(r => r.category === 'assessment'), tool: resources.filter(r => r.category === 'tool'), resource: resources.filter(r => r.category === 'resource') };

  return (
    <div className="space-y-6" data-testid="admin-resources-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Free Resources</h1><p className="text-muted-foreground">Career tools and assessments</p></div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button><Plus className="w-4 h-4 mr-2" /> Add Resource</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Resource</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} /></div>
              <div className="space-y-2"><Label>URL *</Label><Input value={formData.url} onChange={(e) => setFormData(p => ({ ...p, url: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="assessment">Assessment</SelectItem><SelectItem value="tool">Tool</SelectItem><SelectItem value="resource">Resource</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Order</Label><Input type="number" value={formData.order} onChange={(e) => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Active</Label></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit">Save</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {['assessment', 'tool', 'resource'].map((cat) => (
        <Card key={cat}>
          <CardHeader><CardTitle className="capitalize">{cat === 'assessment' ? 'Free Assessments' : cat === 'tool' ? 'Free Career Tools' : 'Additional Resources'}</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="py-4 text-center">Loading...</div> : groupedResources[cat].length === 0 ? <p className="text-muted-foreground text-center py-4">No {cat}s yet</p> : (
              <div className="space-y-3">
                {groupedResources[cat].map((r) => (
                  <div key={r.id} className="flex items-center gap-4 p-3 border rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-medium">{r.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-1">{r.description}</p>
                    </div>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80"><ExternalLink className="w-4 h-4" /></a>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(r)}><Edit className="w-4 h-4" /></Button>
                    <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(r.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminResources;
